import { Box, FormControlLabel, Grid, Typography } from "@mui/material";
import VerticalDrawerContentTemplate from "components/UI/Drawer/VerticalDrawerContentTemplate";
import React from "react";
import AcxExpansion from "components/UI/AcxExpansion";
import { Stack } from "@mui/material";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import { useStore } from "utils/useStore";
import AcxButton from "components/UI/AcxButton";
import { toJS } from "mobx";
import theme from "Theme/AppTheme";
import AcxMainTextField from "components/UI/AcxMainTextField";
import SearchIcon from "@mui/icons-material/Search";
import { FilterCheckbox } from "../shared";
import type { FilterKey } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import FilterTitleWithStatus from "./FilterTitleWithStatus";

export const localStorageFilterInputKey = "additionalFilterInputs";

export const basicFilterCatTitle = "Standard Filters";
export const aiFilterCatTitle = "Core AI Models";
export const addtlMetadataCatTitle = "Additional Metadata";

export interface ConversationFilterCategory {
    title: string;
    subtitle: string;
    filters: ConversationFilter[];
}

export interface ConversationFilter {
    filterTitle: string;
    isActive: boolean; // is active in the conversation filter drawer
    isTemporarilyShown?: boolean;
    id: string;
    storeObjectKey: string;
    /**
     * The Application Filter this entry renders, carried from the drawer registry so a filter's
     * status and info text can be read from its one declaration.
     *
     * Required on the registry (`FilterType`) but optional here, because this shape is serialized
     * to localStorage and entries saved before it existed have none. The merge on load always takes
     * it from the current definition, so it is present by the time anything reads it.
     */
    filterKey?: FilterKey;
    filterLabel?: string;
    resetFunction?: (value: any) => void;
}

export default function ManageFilterInputDrawer() {
    const store = useStore(ConversationsStore);

    const [filterCategories, setFilterCategories] = React.useState<
        ConversationFilterCategory[]
    >(toJS(store.allFilterCategories));

    // Only the display is grouped into Legacy; `filterCategories` stays the declared categories,
    // which is what gets applied and written to localStorage.
    const sortedCategories = React.useMemo(() => {
        return store
            .withLegacyFilterCategory(filterCategories)
            .map((category) => ({
                ...category,
                filters: [...category.filters].sort((a, b) =>
                    a.filterTitle.localeCompare(b.filterTitle),
                ),
            }));
    }, [filterCategories, store]);

    const [searchTerm, setSearchTerm] = React.useState<string>("");

    // Keyed off the filter's own id rather than the category it is rendered under, because the
    // Legacy category is derived for display and has no entry in `filterCategories` to look in.
    const checkboxHandler = (filterId: string) => {
        const filterCatCopy = [...filterCategories];

        const clickedItem = filterCatCopy
            .flatMap((cat) => cat.filters)
            .find((filt) => filt.id === filterId);
        if (clickedItem) {
            clickedItem.isActive = !clickedItem.isActive;
        }

        setFilterCategories(filterCatCopy);
    };

    const renderFilterItem = (filterItem: ConversationFilter) => {
        if (
            !store.authStore.canUserView("AI Search") &&
            filterItem.storeObjectKey === "semanticSearch"
        ) {
            return null;
        }
        let label = filterItem.filterLabel ?? filterItem.filterTitle;
        if (filterItem.storeObjectKey === "mediaType") {
            label = filterItem.filterTitle;
        }
        return (
            <FormControlLabel
                key={filterItem.id}
                // The tag is carried on the label rather than the row so the two identically
                // titled Theme filters can be told apart here the way they are in the filter
                // list. The testid stays the plain title — it identifies the row, not what the
                // row happens to be advertising this release.
                label={
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ columnGap: 0.5 }}
                    >
                        <FilterTitleWithStatus filterKey={filterItem.filterKey}>
                            {label}
                        </FilterTitleWithStatus>
                    </Stack>
                }
                data-testid={`conversations-manage-filters-filter-${label}`}
                control={
                    <FilterCheckbox
                        onChange={() => {
                            checkboxHandler(filterItem.id);
                        }}
                        checked={filterItem.isActive}
                    />
                }
            />
        );
    };

    const handleSearchInputChange = (evt) => {
        setSearchTerm(evt.target.value);
    };

    const handleApplyFilters = () => {
        // Capture filters before applying changes
        const currentActiveFilters: ConversationFilter[] =
            store.allFilterCategories
                .flatMap((category) => category.filters)
                .filter((f) => f.isActive);

        // If filters have applied values, keep them as temporary even if unchecked/marked inactive
        const updatedFilters = store.updateTemporaryFilters(filterCategories);
        store.setAllFilterCategories(updatedFilters);

        const temporaryFilters: ConversationFilter[] = updatedFilters
            .flatMap((category) => category.filters)
            .filter((f) => f.isTemporarilyShown);

        // If an element is in both arrays, it was previously active, is no longer active, but is still temporarily shown
        const hasTempFilterMarkedInactive = temporaryFilters.some((f) =>
            currentActiveFilters.some(
                (cf) => cf.storeObjectKey === f.storeObjectKey,
            ),
        );

        const storageKey = `hasTempFilterMarkedInactiveMessageShown-${store.authStore.orgStore.selectedOrganization?.id}`;

        // Used to only show the message once per session
        // NOTE: Evalstore clears sessionStorage on page refresh
        const hasTempFilterMarkedInactiveMessageShown =
            sessionStorage.getItem(storageKey) === "true";

        if (
            hasTempFilterMarkedInactive &&
            !hasTempFilterMarkedInactiveMessageShown
        ) {
            store.messageStore.logMessage(
                "Some filters are still active that are no longer in your default view.",
                "info",
            );
            sessionStorage.setItem(storageKey, "true");
        }

        // Remove isTemporarilyShown property from filters before saving to localStorage
        // Temporary filters should be cleared on page refresh
        const filterCategoriesWithoutTempProp = updatedFilters.map(
            (category) => ({
                ...category,
                filters: category.filters.map(
                    ({ isTemporarilyShown, ...filter }) => ({
                        ...filter,
                    }),
                ),
            }),
        );

        store.localStorage.setItem(
            localStorageFilterInputKey +
                store.authStore.orgStore.selectedOrganization?.id,
            JSON.stringify(filterCategoriesWithoutTempProp),
        );
        setSearchTerm("");
        store.toggleManageFilterInput();
    };

    const renderCategory = (
        categoryItem: ConversationFilterCategory,
        index: number,
    ) => {
        const filteredFilters = categoryItem.filters.filter((item) => {
            const filterText = item.filterLabel || item.filterTitle;

            return (
                !searchTerm ||
                filterText.toLowerCase().includes(searchTerm.toLowerCase())
            );
        });

        if (filteredFilters.length === 0) {
            return null;
        }

        return (
            <Grid key={index}>
                <AcxExpansion
                    expanded={true}
                    header={
                        <Stack>
                            <Typography
                                sx={{ fontWeight: "bold", fontSize: 14 }}
                            >
                                {categoryItem.title}
                            </Typography>

                            <Typography
                                sx={{
                                    fontWeight: "normal",
                                    fontSize: 12,
                                    mt: 0.5,
                                }}
                            >
                                {categoryItem.subtitle}
                            </Typography>
                        </Stack>
                    }
                    body={
                        <Stack sx={{ gap: 1.5 }}>
                            {filteredFilters.map(renderFilterItem)}
                        </Stack>
                    }
                    rootStyle={{
                        background: theme.palette.gray[50],
                        padding: theme.spacing(2),
                    }}
                    headerSx={{
                        background: theme.palette.gray[50],
                        marginBottom: theme.spacing(2),
                    }}
                    conversationVariant
                    chevronExpandedSx={{
                        transform: "rotate(180deg)",
                    }}
                />
            </Grid>
        );
    };

    return (
        <VerticalDrawerContentTemplate
            content={
                <Grid
                    container
                    sx={{
                        height: "100%",
                    }}
                    flexDirection={"column"}
                >
                    <AcxMainTextField
                        id="search filter input"
                        placeholderText="Search filters"
                        fullWidth
                        startAdornment={<SearchIcon color="disabled" />}
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                        containerStyle={{
                            padding: theme.spacing(1, 1, 2),
                            borderBottom: `1px solid ${theme.palette.gray[200]}`,
                            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.07)",
                        }}
                    />
                    <Box
                        sx={{
                            overflowY: "auto",
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        {sortedCategories.map(renderCategory)}
                    </Box>

                    <Box
                        sx={{
                            borderTop: `1px solid ${theme.palette.gray[200]}`,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            p: 2,
                        }}
                    >
                        <AcxButton
                            fullWidth
                            id="conversations-manage-filter-input-cancel-changes-button"
                            color="white"
                            onClick={() => {
                                setFilterCategories(
                                    toJS(store.allFilterCategories),
                                );
                                setSearchTerm("");
                                store.toggleManageFilterInput();
                            }}
                        >
                            Cancel
                        </AcxButton>
                        <AcxButton
                            fullWidth
                            id="conversations-manage-filter-input-apply-changes-button"
                            data-testid="conversations-manage-filter-input-apply-changes-button"
                            color="primary"
                            onClick={handleApplyFilters}
                        >
                            Update
                        </AcxButton>
                    </Box>
                </Grid>
            }
            title="Manage Filters"
            subTitle="You can select which filters are in your default view here."
            customMarginTop="72px"
            width="400px"
            height="calc(100% - 75px)"
            wrapSubtitle
            contentStyles={{
                paddingLeft: 0,
                paddingRight: 0,
                display: "flex",
                flexDirection: "column",
                height: "calc(100% - 116px)",
                borderLeft: `1px solid ${theme.palette.gray[200]}`,
            }}
            titleStyles={{
                padding: theme.spacing(4, 1, 1, 2),
                borderLeft: `1px solid ${theme.palette.gray[200]}`,
            }}
            titleTextStyle={{
                fontWeight: theme.typography.fontWeightSemibold,
                fontSize: "18px",
                fontFamily: theme.typography.fontFamily,
            }}
            subTitleStyles={{
                padding: theme.spacing(1, 2, 0),
                borderLeft: `1px solid ${theme.palette.gray[200]}`,
            }}
            subtitleTextStyle={{
                fontSize: "14px",
                fontWeight: 400,
            }}
            arrowCloseIcon
            onClose={store.toggleManageFilterInput}
            hideTitleBorder
        />
    );
}
